import {
  createContext,
  createMemo,
  createSignal,
  useContext,
  type Accessor,
  type JSX,
  type ParentComponent,
} from "solid-js";
import enMessages from "./messages/en.json";
import esMessages from "./messages/es.json";

export type Locale = "en" | "es";

export const LOCALE_STORAGE_KEY = "ikamiro.locale";

const dicts: Record<Locale, Record<string, string>> = {
  en: enMessages as Record<string, string>,
  es: esMessages as Record<string, string>,
};

function resolveInitialLocale(): Locale {
  if (typeof window === "undefined") return "en";
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored === "en" || stored === "es") return stored;
  } catch {
    // localStorage may throw in private mode / locked-down contexts.
  }
  const nav = window.navigator?.language?.toLowerCase() ?? "";
  if (nav.startsWith("es")) return "es";
  return "en";
}

export type TFn = (key: string, params?: Record<string, string | number>) => string;

interface LocaleCtx {
  locale: Accessor<Locale>;
  setLocale: (next: Locale) => void;
  t: TFn;
}

const Ctx = createContext<LocaleCtx>();

// Module-scoped so the warn-once contract holds across re-mounts within a session.
const warned = new Set<string>();

function interpolate(raw: string, params?: Record<string, string | number>): string {
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, k: string) =>
    k in params ? String(params[k]) : `{${k}}`,
  );
}

export const LocaleProvider: ParentComponent = (props): JSX.Element => {
  const [locale, setLocaleSignal] = createSignal<Locale>(resolveInitialLocale());

  const setLocale = (next: Locale) => {
    setLocaleSignal(next);
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      // ignore quota / disabled-storage
    }
  };

  const enDict = dicts.en;

  // createMemo so the active dict reference updates reactively with locale().
  const activeDict = createMemo(() => dicts[locale()]);

  const t: TFn = (key, params) => {
    const dict = activeDict();
    const hit = dict[key];
    if (hit != null) return interpolate(hit, params);

    const fallback = enDict[key];
    if (fallback != null) {
      if (!warned.has(key)) {
        warned.add(key);
        console.warn(`[i18n] missing key "${key}" in ${locale()} — falling back to EN`);
      }
      return interpolate(fallback, params);
    }

    if (!warned.has(key)) {
      warned.add(key);
      console.warn(`[i18n] missing key "${key}" in EN and ${locale()}`);
    }
    return key;
  };

  return <Ctx.Provider value={{ locale, setLocale, t }}>{props.children}</Ctx.Provider>;
};

export function useLocale(): LocaleCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useLocale must be called inside <LocaleProvider>");
  return ctx;
}

export function useT(): TFn {
  return useLocale().t;
}
